import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquare, Send, Trash2, User } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Comments({ entityType, entityId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (entityId) {
      fetchComments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/comments/${entityType}/${entityId}`, { withCredentials: true });
      setComments(response.data);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePostComment = async () => {
    if (!newComment.trim()) return;
    
    setPosting(true);
    try {
      await axios.post(`${API}/comments`, {
        entity_type: entityType,
        entity_id: entityId,
        content: newComment.trim()
      }, { withCredentials: true });
      
      setNewComment("");
      fetchComments();
      toast.success("Comment added!");
    } catch (error) {
      console.error("Failed to post comment:", error);
      toast.error("Failed to add comment");
    } finally {
      setPosting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    
    try {
      await axios.delete(`${API}/comments/${commentId}`, { withCredentials: true });
      fetchComments();
      toast.success("Comment deleted");
    } catch (error) {
      console.error("Failed to delete comment:", error);
      toast.error("Failed to delete comment");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-4" data-testid="comments-section">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="w-5 h-5 text-[#00E599]" />
        <h3 className="text-lg font-semibold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
          Comments ({comments.length})
        </h3>
      </div>

      {/* Comment Input */}
      <div className="space-y-2">
        <Textarea
          data-testid="comment-input"
          placeholder="Add a comment..."
          className="bg-[#0B0F1A] border-white/10 text-white rounded-sm min-h-[80px] resize-none"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.metaKey) {
              handlePostComment();
            }
          }}
        />
        <div className="flex justify-between items-center">
          <span className="text-xs text-slate-500">Press ⌘+Enter to submit</span>
          <Button
            data-testid="post-comment-btn"
            size="sm"
            className="bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold"
            onClick={handlePostComment}
            disabled={posting || !newComment.trim()}
          >
            <Send className="w-4 h-4 mr-2" />
            {posting ? "Posting..." : "Post"}
          </Button>
        </div>
      </div>

      {/* Comments List */}
      <ScrollArea className="h-[300px]">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-500">
            Loading comments...
          </div>
        ) : comments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-slate-500">
            <MessageSquare className="w-10 h-10 mb-2 opacity-50" />
            <p>No comments yet</p>
            <p className="text-xs">Be the first to comment!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div
                key={comment.id}
                className="p-4 bg-white/5 rounded-sm border border-white/5"
                data-testid={`comment-${comment.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    {comment.user_picture ? (
                      <img
                        src={comment.user_picture}
                        alt={comment.user_name}
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-[#00E599]/10 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-[#00E599]" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{comment.user_name || "Anonymous"}</span>
                        <span className="text-xs text-slate-500">{formatDate(comment.created_at)}</span>
                      </div>
                      <p className="text-sm text-slate-300 whitespace-pre-wrap">{comment.content}</p>
                    </div>
                  </div>
                  {user && comment.user_id === user.user_id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm h-8 w-8 p-0"
                      onClick={() => handleDeleteComment(comment.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
